import { DOCUMENT, isPlatformBrowser } from '@angular/common'
import { computed, DestroyRef, inject, Injectable, PLATFORM_ID, signal, Signal } from '@angular/core'
import { Observable } from 'rxjs'

@Injectable({ providedIn: 'root' })
export class ResponsiveUi {
	private readonly destroyRef = inject(DestroyRef)
	private readonly document = inject(DOCUMENT)
	private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID))

	private readonly smallBreakpoint = this.readCssPropertyAsNumber('--small-breakpoint')
	readonly pageHeaderBarHeight = computed(() => {
		this.isSmallViewport()
		return this.readCssPropertyAsNumber('--page-header-bar-height')
	})

	/** A signal that stays updated with the current viewport size. */
	readonly isSmallViewport = this.createBreakpointMediaQuerySignal(this.smallBreakpoint, 'max', this.destroyRef)

	/** Creates a new ResizeObserver and converts it to an Observable. */
	observeResize(element: HTMLElement): Observable<ResizeObserverEntry[]> {
		return new Observable(observer => {
			const resizeObserver = new ResizeObserver(entries => observer.next(entries))
			resizeObserver.observe(element)
			return () => resizeObserver.disconnect()
		})
	}

	/** @returns A boolean signal that indicates whether or not the viewport
	 * width matches the min/max-width media query at `breakpoint`. */
	createBreakpointMediaQuerySignal(breakpoint: number, limit: 'min'|'max', destroyRef: DestroyRef): Signal<boolean> {
		if (!this.isBrowser) return signal(false).asReadonly()

		const mediaQueryList = window.matchMedia(`(${limit}-width: ${breakpoint}px)`)
		const mediaQuerySignal = signal(mediaQueryList.matches)
		const updateSignal = ({ matches }: MediaQueryListEvent) => mediaQuerySignal.set(matches)
		mediaQueryList.addEventListener('change', updateSignal)
		destroyRef.onDestroy(() => mediaQueryList.removeEventListener('change', updateSignal))
		return mediaQuerySignal.asReadonly()
	}

	private readCssPropertyAsNumber(property: string): number {
		if (!this.isBrowser) return 0
		return parseInt(getComputedStyle(this.document.documentElement).getPropertyValue(property), 10)
	}

}
